import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Zap, Clock, X, Check, Cpu, MemoryStick, HardDrive, Gauge } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../LanguageContext';
import { useNavigate } from 'react-router-dom';

const BenchmarkPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [isOptimized, setIsOptimized] = useState(false);
  // also init refs accordingly
  const [animatedFPS, setAnimatedFPS] = useState({ average: 320.6, low1: 135.8, low02: 112.5 });
  const rafRef = useRef(null);
  const stateRef = useRef({
    current: { average: 320.6, low1: 135.8, low02: 112.5 },
    target:  { average: 320.6, low1: 135.8, low02: 112.5 },
    nextRetarget: 0,
    mode: 'before',
  });

  const benchmarkResults = useMemo(() => ({
    cpu: 'AMD Ryzen 7 5800X',
    gpu: 'NVIDIA RTX 3060',
    before: { average: 320.6, low1: 135.8, low02: 112.5 },
    after:  { average: 549.5, low1: 275.1, low02: 178.0 },
  }), []);

  const maxFPS = 600;

  const gains = useMemo(() => {
    const b = benchmarkResults.before;
    const a = benchmarkResults.after;
    const pct = (x, y) => Math.round(((y - x) / x) * 1000) / 10;
    return {
      average: { delta: +(a.average - b.average).toFixed(1), pct: pct(b.average, a.average) },
      low1:    { delta: +(a.low1 - b.low1).toFixed(1),       pct: pct(b.low1, a.low1) },
      low02:   { delta: +(a.low02 - b.low02).toFixed(1),     pct: pct(b.low02, a.low02) },
    };
  }, [benchmarkResults]);

  // Smooth animation: tweens between Avant <-> Après + subtle realistic micro-fluctuations
  useEffect(() => {
    stateRef.current.mode = isOptimized ? 'after' : 'before';
    stateRef.current.nextRetarget = performance.now() + 300;

    const pickTarget = (mode) => {
      const base = mode === 'after' ? benchmarkResults.after : benchmarkResults.before;
      if (mode === 'after') {
        // AFTER: very subtle micro-jitter (±1.5 fps avg) — feels alive but stable
        const j = (a) => (Math.random() - 0.5) * a;
        return {
          average: base.average + j(3.0),
          low1:    base.low1    + j(2.2),
          low02:   base.low02   + j(1.5),
        };
      }
      // BEFORE: visible realistic instability with notable dips (shows unstable PC)
      const r = Math.random();
      const dip = r < 0.06 ? (30 + Math.random() * 20)        // big stutter ~6%
                : r < 0.22 ? (12 + Math.random() * 14)        // mid stutter ~16%
                : 0;
      const j = (a) => (Math.random() - 0.5) * a;
      return {
        average: base.average + j(14) - dip,
        low1:    base.low1    + j(10) - dip * 0.85,
        low02:   base.low02   + j(7)  - dip * 0.7,
      };
    };

    let lastTs = performance.now();

    const frame = (now) => {
      const dt = Math.min((now - lastTs) / 1000, 0.1);
      lastTs = now;
      const mode = stateRef.current.mode;

      if (now >= stateRef.current.nextRetarget) {
        stateRef.current.target = pickTarget(mode);
        const interval = mode === 'after' ? (900 + Math.random() * 500) : (380 + Math.random() * 280);
        stateRef.current.nextRetarget = now + interval;
      }

      // Smooth approach (slower = smoother bars, no jitter on display)
      const k = mode === 'after' ? 2.8 : 4.2;
      const c = stateRef.current.current;
      const T = stateRef.current.target;
      const a = Math.min(k * dt, 1);
      c.average = Math.max(0, c.average + (T.average - c.average) * a);
      c.low1    = Math.max(0, c.low1    + (T.low1    - c.low1)    * a);
      c.low02   = Math.max(0, c.low02   + (T.low02   - c.low02)   * a);

      setAnimatedFPS({
        average: Math.round(c.average * 10) / 10,
        low1:    Math.round(c.low1 * 10) / 10,
        low02:   Math.round(c.low02 * 10) / 10,
      });

      rafRef.current = requestAnimationFrame(frame);
    };

    rafRef.current = requestAnimationFrame(frame);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOptimized]);

  const MetricBar = ({ label, value, max }) => {
    const pct = Math.min((value / max) * 100, 100);
    return (
      <div>
        {/* Label + Value row */}
        <div className="flex items-end justify-between mb-2.5 md:mb-3">
          <span className="text-sm md:text-base text-gray-300 font-medium tracking-wide">{label}</span>
          <span className="font-mono tabular-nums">
            <span className="text-3xl md:text-5xl font-bold text-white">{value.toFixed(1)}</span>
            <span className="text-xs md:text-sm text-gray-500 ml-1.5 font-normal">FPS</span>
          </span>
        </div>
        {/* Thick clean bar */}
        <div className="relative h-2.5 md:h-3 bg-white/[0.04] rounded-full overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full"
            style={{
              width: `${pct}%`,
              boxShadow: '0 0 18px rgba(8, 145, 178, 0.45)',
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen pt-20 md:pt-24 pb-12 md:pb-16 px-4 md:px-8">
      {/* Header */}
      <div className="text-center mb-8 md:mb-14 animate-fadeInUp">
        <span className="inline-block px-3 md:px-4 py-1 md:py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-[10px] md:text-xs font-semibold uppercase tracking-[0.18em] mb-4 md:mb-6">
          {t('benchmark.badge')}
        </span>
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-3 md:mb-4 tracking-tight">
          {t('benchmark.title')} <span className="text-cyan-400">{t('benchmark.titleHighlight')}</span>
        </h1>
        <p className="text-gray-400 text-sm md:text-lg max-w-2xl mx-auto px-4">
          {t('benchmark.subtitle')}
        </p>
      </div>

      <div className="max-w-4xl mx-auto space-y-6 md:space-y-8">

        {/* Main Benchmark Card */}
        <div className="relative bg-gradient-to-b from-zinc-900/70 to-zinc-950/80 border border-white/10 rounded-2xl md:rounded-3xl p-4 md:p-8 animate-fadeInUp backdrop-blur-sm overflow-hidden">
          {/* Ambient glow */}
          <div className="pointer-events-none absolute -top-40 -right-40 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
          <div className="pointer-events-none absolute -bottom-40 -left-40 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>

          {/* Glass Toggle */}
          <div className="relative flex justify-center mb-6 md:mb-8">
            <div className="inline-flex items-center p-1 rounded-full bg-black/40 border border-white/10 backdrop-blur">
              <button
                onClick={() => setIsOptimized(false)}
                data-testid="toggle-avant"
                className={`relative px-5 md:px-7 py-2 rounded-full text-xs md:text-sm font-semibold transition-colors duration-300 ${
                  !isOptimized ? 'text-white' : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                {!isOptimized && (
                  <span className="absolute inset-0 rounded-full bg-white/10 shadow-[0_0_0_1px_rgba(255,255,255,0.1)_inset]"></span>
                )}
                <span className="relative">{t('benchmark.before')}</span>
              </button>
              <button
                onClick={() => setIsOptimized(true)}
                data-testid="toggle-apres"
                className={`relative px-5 md:px-7 py-2 rounded-full text-xs md:text-sm font-semibold transition-colors duration-300 ${
                  isOptimized ? 'text-white' : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                {isOptimized && (
                  <span className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 shadow-[0_0_30px_-6px_rgba(6,182,212,0.6)]"></span>
                )}
                <span className="relative">{t('benchmark.after')}</span>
              </button>
            </div>
          </div>

          {/* Hardware strip */}
          <div className="relative flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-6 md:mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/10 text-xs md:text-sm">
              <Cpu className="w-3.5 h-3.5 text-green-400" />
              <span className="text-gray-200">{benchmarkResults.cpu}</span>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/10 text-xs md:text-sm">
              <Gauge className="w-3.5 h-3.5 text-cyan-400" />
              <span className="text-gray-200">{benchmarkResults.gpu}</span>
            </div>
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs md:text-sm transition-all duration-500 ${
              isOptimized
                ? 'bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 opacity-100'
                : 'bg-white/[0.02] border border-white/5 text-gray-500 opacity-60'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isOptimized ? 'bg-cyan-400 animate-pulse' : 'bg-gray-600'}`}></span>
              {isOptimized ? t('benchmark.optimizedBy') : 'Stock'}
            </div>
          </div>

          {/* FPS Metrics */}
          <div className="relative space-y-7 md:space-y-9">
            <MetricBar
              label={t('benchmark.averageFPS')}
              value={animatedFPS.average}
              max={maxFPS}
            />
            <MetricBar
              label={t('benchmark.low1FPS')}
              value={animatedFPS.low1}
              max={maxFPS}
            />
            <MetricBar
              label={t('benchmark.low02FPS')}
              value={animatedFPS.low02}
              max={maxFPS}
            />
          </div>
        </div>

        {/* Performance Gains — only when 'Après' is active — Tweakslab-style cards */}
        {isOptimized && (
        <div className="animate-fadeIn">
          <p className="text-center text-xs md:text-sm text-gray-400 mb-4 md:mb-5 uppercase tracking-[0.18em]">{t('benchmark.performanceGains')}</p>
          <div className="grid grid-cols-3 gap-2 md:gap-4">
            {[
              { label: t('benchmark.avgFPS'), delta: gains.average.delta, pct: gains.average.pct },
              { label: '1% Low',              delta: gains.low1.delta,    pct: gains.low1.pct    },
              { label: '0.2% Low',            delta: gains.low02.delta,   pct: gains.low02.pct   },
            ].map((g, i) => (
              <div key={i} className="bg-white/[0.02] border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 text-center hover:border-cyan-500/30 transition-colors">
                <div className="text-2xl md:text-4xl font-bold text-cyan-400 tabular-nums font-mono leading-none">+{g.pct}%</div>
                <div className="text-[10px] md:text-xs text-gray-300 mt-2 md:mt-3 tabular-nums font-mono">+{g.delta} FPS</div>
                <div className="text-[10px] md:text-xs text-gray-500 mt-1 uppercase tracking-wider">{g.label}</div>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Before / After Comparison */}
        <div className="animate-fadeInUp" style={{ animationDelay: '400ms' }}>
          <h3 className="text-base md:text-lg font-semibold text-white mb-3 md:mb-4 tracking-tight">{t('benchmark.comparison')}</h3>
          <div className="grid md:grid-cols-2 gap-3 md:gap-4">
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4 md:p-6">
              <div className="flex items-center gap-2 mb-3 md:mb-4">
                <div className="w-7 h-7 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                  <X className="w-4 h-4 text-red-400" />
                </div>
                <h4 className="text-sm md:text-base font-semibold text-gray-200">{t('benchmark.before')}</h4>
              </div>
              <ul className="space-y-2 text-xs md:text-sm text-gray-400">
                {['freezes','latency','stutters','drops','unoptimized'].map(k => (
                  <li key={k} className="flex items-start gap-2">
                    <span className="mt-1.5 w-1 h-1 bg-red-400/60 rounded-full shrink-0"></span>
                    {t(`benchmark.beforeList.${k}`)}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-b from-cyan-500/[0.05] to-transparent border border-cyan-500/20 rounded-xl p-4 md:p-6">
              <div className="flex items-center gap-2 mb-3 md:mb-4">
                <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                  <Check className="w-4 h-4 text-cyan-300" />
                </div>
                <h4 className="text-sm md:text-base font-semibold text-cyan-200">{t('benchmark.after')}</h4>
              </div>
              <ul className="space-y-2 text-xs md:text-sm text-gray-300">
                {['smooth','minimalLatency','noStutter','stableFPS','maxOptimization'].map(k => (
                  <li key={k} className="flex items-start gap-2">
                    <Check className="mt-0.5 w-3 h-3 text-cyan-400 shrink-0" />
                    {t(`benchmark.afterList.${k}`)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* System Info Card */}
        <div className="bg-white/[0.02] border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 animate-fadeInUp backdrop-blur-sm" style={{ animationDelay: '500ms' }}>
          <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-5">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
              <Zap className="w-4 h-4 md:w-5 md:h-5 text-cyan-300" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-white tracking-tight">{t('benchmark.systemConfig')}</h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
            <div>
              <p className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1"><Cpu className="w-3 h-3" /> {t('benchmark.processor')}</p>
              <p className="text-xs md:text-sm text-white font-medium">{benchmarkResults.cpu}</p>
            </div>
            <div>
              <p className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1"><Gauge className="w-3 h-3" /> {t('benchmark.gpu')}</p>
              <p className="text-xs md:text-sm text-white font-medium">{benchmarkResults.gpu}</p>
            </div>
            <div>
              <p className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1"><MemoryStick className="w-3 h-3" /> {t('benchmark.ram')}</p>
              <p className="text-xs md:text-sm text-white font-medium">16 GB DDR4 3200 MHz</p>
            </div>
            <div>
              <p className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1"><HardDrive className="w-3 h-3" /> {t('benchmark.storage')}</p>
              <p className="text-xs md:text-sm text-white font-medium">Samsung 970 EVO 1TB</p>
            </div>
          </div>

          <Button
            onClick={() => navigate('/services')}
            className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-3 md:py-4 rounded-xl transition-all duration-200 text-sm md:text-base shadow-[0_0_30px_-6px_rgba(6,182,212,0.45)]"
          >
            <Clock className="w-4 h-4 md:w-5 md:h-5 mr-2" />
            {t('benchmark.newTest')}
          </Button>
        </div>

        {/* Recommendation */}
        <div className="p-3 md:p-4 bg-white/[0.02] border border-white/10 rounded-xl animate-fadeInUp" style={{ animationDelay: '600ms' }}>
          <p className="text-xs md:text-sm text-gray-300">
            <span className="font-semibold text-cyan-300">{t('benchmark.recommendation')}</span> {t('benchmark.recommendationText')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default BenchmarkPage;
